import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus } from "@cx/ui";
import type { PriceListView } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";
import { EMPTY_PRICE_LIST, PriceListDialog } from "./PriceListDialog";
import { KIND_LABEL, ruleText, validityText } from "./labels";

/** Listas de preços (RF-CAD-04): Taxas, Por período e Customizada. */
export function PriceListsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const perms = me.permissions as Permissions;
  const list = useQuery({
    queryKey: ["price-lists", companyId],
    queryFn: () => api<{ data: PriceListView[] }>("/price-lists"),
  });
  const [creating, setCreating] = useState(false);
  return (
    <>
      <ContentHeader
        title="Listas de preços"
        breadcrumb={["Cadastros", "Listas de preços"]}
        actions={
          can(perms, "price_lists", "create") && (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Incluir lista de preço
            </button>
          )
        }
      />
      <div className="box card">
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && list.data.data.length === 0 && (
          <EmptyState
            title="Nenhuma lista de preço"
            description="Sem lista, vale o preço de venda do produto."
            actions={
              can(perms, "price_lists", "create")
                ? [{ label: "Incluir lista de preço", onClick: () => setCreating(true) }]
                : []
            }
          />
        )}
        {list.isSuccess && list.data.data.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Listas de preços">
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Regra</th>
                  <th scope="col">Vigência</th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <Link to="/cadastros/listas-de-precos/$id" params={{ id: l.id }}>
                        {l.name}
                      </Link>
                    </td>
                    <td>{KIND_LABEL[l.kind]}</td>
                    <td>{ruleText(l)}</td>
                    <td>{validityText(l.validFrom, l.validTo)}</td>
                    <td>{l.active ? "Ativa" : "Inativa"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {creating && (
        <PriceListDialog
          initial={EMPTY_PRICE_LIST}
          onClose={() => setCreating(false)}
          onSaved={(id) => {
            qc.invalidateQueries({ queryKey: ["price-lists", companyId] });
            navigate({ to: "/cadastros/listas-de-precos/$id", params: { id } });
          }}
        />
      )}
    </>
  );
}
